import React, { useEffect, useRef, useState } from "react";
import { Scissors, Check, Maximize, RotateCcw, X } from "lucide-react";
import { AnnotateToolbar } from "./AnnotateToolbar";
import { COLORS, drawAll, drawShape } from "./annotate";

/* 截图裁剪 + 标注层：无工具 → 拖拽画选区（选区外压暗）；选工具 → 在图上画方框 / 箭头 / 画笔 / 文字；确定时把选区 + 标注合成为 PNG */
export function CropOverlay({ shot, zh, onDone, onCancel, onRetake }) {
  const T = (z, e) => (zh ? z : e);
  const imgRef = useRef(null);
  const cvRef = useRef(null);
  const [sel, setSel] = useState(null);
  const [fit, setFit] = useState({ w: 0, h: 0 });
  const [tool, setTool] = useState(null);
  const [color, setColor] = useState(COLORS[0]);
  const [width, setWidth] = useState(4);
  const [shapes, setShapes] = useState([]);
  const [cur, setCur] = useState(null);
  const [textAt, setTextAt] = useState(null);
  const drag = useRef(null);
  const textRef = useRef(null);

  useEffect(() => {
    const calc = () => { const W = window.innerWidth - 32, H = window.innerHeight - 170; const s = Math.min(W / shot.canvas.width, H / shot.canvas.height, 1); setFit({ w: Math.round(shot.canvas.width * s), h: Math.round(shot.canvas.height * s) }); };
    calc(); window.addEventListener("resize", calc); return () => window.removeEventListener("resize", calc);
  }, [shot]);
  const scale = fit.w ? shot.canvas.width / fit.w : 1;
  const rect = sel && { x: Math.min(sel.x0, sel.x1), y: Math.min(sel.y0, sel.y1), w: Math.abs(sel.x1 - sel.x0), h: Math.abs(sel.y1 - sel.y0) };
  const hasSel = rect && rect.w >= 4 && rect.h >= 4;

  useEffect(() => {
    const c = cvRef.current; if (!c || !fit.w) return;
    const ctx = c.getContext("2d"); ctx.clearRect(0, 0, c.width, c.height);
    drawAll(ctx, shapes); if (cur) drawShape(ctx, cur);
  }, [shapes, cur, fit]);
  useEffect(() => { if (textAt) setTimeout(() => textRef.current?.focus(), 30); }, [textAt]);

  const pos = (e) => { const b = imgRef.current.getBoundingClientRect(); return { x: Math.max(0, Math.min(e.clientX - b.left, b.width)), y: Math.max(0, Math.min(e.clientY - b.top, b.height)) }; };
  const commitText = () => { const t = (textRef.current?.value || "").trim(); if (textAt && t) setShapes((s) => [...s, { type: "text", x0: textAt.x, y0: textAt.y, text: t, color, fontSize: 14 + width * 2, width }]); setTextAt(null); };
  const down = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    if (textAt) { commitText(); return; }
    const p = pos(e);
    if (tool === "text") { setTextAt(p); return; }
    drag.current = p;
    if (tool) setCur(tool === "pen" ? { type: "pen", points: [[p.x, p.y]], color, width } : { type: tool, x0: p.x, y0: p.y, x1: p.x, y1: p.y, color, width });
    else setSel({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    if (!drag.current) return;
    const p = pos(e);
    if (tool === "pen") setCur((c) => c && { ...c, points: [...c.points, [p.x, p.y]] });
    else if (tool) setCur((c) => c && { ...c, x1: p.x, y1: p.y });
    else setSel({ x0: drag.current.x, y0: drag.current.y, x1: p.x, y1: p.y });
  };
  const up = () => {
    if (!drag.current) return;
    drag.current = null;
    if (tool) { setCur((c) => { if (c && (c.type === "pen" ? c.points.length > 1 : Math.hypot(c.x1 - c.x0, c.y1 - c.y0) >= 3)) setShapes((s) => [...s, c]); return null; }); }
    else setSel((s) => (s && (Math.abs(s.x1 - s.x0) < 4 || Math.abs(s.y1 - s.y0) < 4) ? null : s));
  };
  const confirm = (full) => {
    const src = shot.canvas;
    const r = !full && hasSel ? { x: Math.round(rect.x * scale), y: Math.round(rect.y * scale), w: Math.max(1, Math.round(rect.w * scale)), h: Math.max(1, Math.round(rect.h * scale)) } : { x: 0, y: 0, w: src.width, h: src.height };
    const out = document.createElement("canvas"); out.width = r.w; out.height = r.h;
    const ctx = out.getContext("2d");
    ctx.drawImage(src, r.x, r.y, r.w, r.h, 0, 0, r.w, r.h);
    drawAll(ctx, shapes, scale, r.x / scale, r.y / scale);
    out.toBlob((b) => b && onDone(b), "image/png");
  };
  useEffect(() => {
    const k = (e) => {
      if (textRef.current || document.activeElement?.tagName === "INPUT" || document.activeElement?.tagName === "TEXTAREA") return;
      if (e.key === "Escape") { if (tool) setTool(null); else onCancel(); }
      else if (e.key === "Enter") confirm(false);
      else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") { e.preventDefault(); setShapes((s) => s.slice(0, -1)); }
    };
    window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k);
  }); // eslint-disable-line
  const btn = (cls) => `inline-flex items-center gap-1 h-8 px-3 rounded-full text-xs font-semibold transition-colors ${cls}`;
  const cursor = tool === "text" ? "cursor-text" : tool ? "cursor-crosshair" : "cursor-crosshair";
  return (
    <div data-testid="chat-screenshot-overlay" data-tool={tool || ""} data-shapes={shapes.length} className="fixed inset-0 z-[10002] bg-slate-950/85 flex flex-col items-center select-none" onPointerDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="mobile-safe-topbar h-11 shrink-0 flex items-center gap-3 text-xs text-white/80" data-testid="chat-screenshot-hint">
        <Scissors className="h-3.5 w-3.5" />
        {hasSel ? <span data-testid="chat-screenshot-size">{Math.round(rect.w * scale)} × {Math.round(rect.h * scale)} px{shapes.length ? ` · ${shapes.length} ${T("处标注", "marks")}` : ""}</span>
          : tool ? T(`${{ rect: "拖拽画方框", arrow: "拖拽画箭头", pen: "按住涂画", text: "点击位置输入文字" }[tool]} · 再点工具或 Esc 退出工具后可重新框选`, "Draw on the image · Esc to leave the tool")
            : T("按住鼠标框选要发送的区域；用下方工具标注（方框 / 箭头 / 画笔 / 文字）· Esc 取消 · Enter 确定", "Drag to select; annotate with the tools below · Esc cancel · Enter confirm")}
      </div>
      <div className="relative shadow-2xl" style={{ width: fit.w, height: fit.h }}>
        <img ref={imgRef} src={shot.url} alt="" draggable={false} data-testid="chat-screenshot-image" style={{ width: fit.w, height: fit.h }} className={`block touch-none ${cursor}`} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} />
        <canvas ref={cvRef} width={fit.w} height={fit.h} data-testid="chat-annotate-canvas" className="absolute inset-0 pointer-events-none" />
        {hasSel && <div data-testid="chat-screenshot-selection" className="absolute border-2 border-[#4F6BFF] pointer-events-none" style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h, boxShadow: "0 0 0 9999px rgba(2,6,23,.55)" }} />}
        {textAt && (
          <input ref={textRef} data-testid="chat-annotate-text-input" defaultValue="" placeholder={T("输入文字，Enter 确定", "Type, Enter to place")} onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") { e.preventDefault(); commitText(); } else if (e.key === "Escape") setTextAt(null); }} onBlur={commitText} onPointerDown={(e) => e.stopPropagation()}
            style={{ left: textAt.x, top: textAt.y, color, fontSize: 14 + width * 2 }} className="absolute bg-transparent border border-dashed border-white/60 rounded px-1 font-semibold outline-none min-w-[120px]" />
        )}
      </div>
      <div className="h-[118px] shrink-0 flex flex-col items-center justify-center gap-2" onPointerDown={(e) => e.stopPropagation()}>
        <AnnotateToolbar zh={zh} tool={tool} setTool={(t) => { setTool(t); setTextAt(null); }} color={color} setColor={setColor} width={width} setWidth={setWidth} canUndo={shapes.length > 0} onUndo={() => setShapes((s) => s.slice(0, -1))} onClear={() => setShapes([])} />
        <div className="flex items-center gap-2">
          <button type="button" data-testid="chat-screenshot-cancel" onClick={onCancel} className={btn("bg-white/10 text-white hover:bg-white/20")}><X className="h-3.5 w-3.5" />{T("取消", "Cancel")}</button>
          <button type="button" data-testid="chat-screenshot-retake" onClick={onRetake} className={btn("bg-white/10 text-white hover:bg-white/20")}><RotateCcw className="h-3.5 w-3.5" />{T("重截", "Retake")}</button>
          <button type="button" data-testid="chat-screenshot-full" onClick={() => confirm(true)} className={btn("bg-white/10 text-white hover:bg-white/20")}><Maximize className="h-3.5 w-3.5" />{T("发送整张", "Full screen")}</button>
          <button type="button" data-testid="chat-screenshot-confirm" onClick={() => confirm(false)} className={btn("bg-gradient-to-r from-[#4F6BFF] to-[#7B5CFF] text-white shadow-md shadow-indigo-500/30")}><Check className="h-3.5 w-3.5" />{hasSel ? T("确定（发送选区）", "Use selection") : T("确定", "OK")}</button>
        </div>
      </div>
    </div>
  );
}
